import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { RailwayZones } from '../../../station-track-visualizer/models/model';
import { StationSurveyStepperService } from './station-survey-stepper.service';
import { isNullOrUndefined } from '../../../../../../usermgmt/src/lib/modules/shared';

@Injectable()
export class StationDetailsSyncService {
  constructor(private readonly stepperService: StationSurveyStepperService) {}

  /**
   * Emits after every station ID / Name / Code sync so that
   * sibling components (IP-port table, tag-summary table, visualizer)
   * can call detectChanges() and instantly reflect the new values.
   */
  readonly stationDetailsUpdated$ = new Subject<void>();

  private findAdjacentByUuidOrId(
    stationData: any,
    uuid: string | null | undefined,
    id: string | null | undefined
  ): any {
    if (!stationData?.adjacent_stations) return null;
    return stationData.adjacent_stations.find((s: any) => {
      if (uuid && s.station_uuid === uuid) return true;
      if (id && String(s.station_id) === String(id)) return true;
      return false;
    }) ?? null;
  }

  private isCurrentStation(stationData: any, stationId: string): boolean {
    return String(stationData?.['Station ID']) === String(stationId);
  }

  private findAdjacentById(stationData: any, stationId: string): any {
    return stationData?.adjacent_stations?.find(
      (s: any) => String(s?.station_id) === String(stationId)
    ) ?? null;
  }

  /**
   * Determine which field populates the `name` column in station_tag_summary
   * for a given zone / station type, mirroring _resolve_station_display_name
   * and _build_block_column in Python.
   *
   * Returns 'station_name' | 'station_code'
   */
  private nameFieldForZone(
    zone: string,
    isCurrent: boolean,
    isBlock: boolean
  ): 'station_name' | 'station_code' {
    const z = (zone ?? '').trim().toLowerCase();
    if (z === RailwayZones.WESTERN_RAILWAY.toLowerCase()) return 'station_code';
    if (z === RailwayZones.WEST_CENTRAL_RAILWAY.toLowerCase()) return 'station_name';
    if (z === RailwayZones.CENTRAL_RAILWAY.toLowerCase()) return isCurrent ? 'station_name' : 'station_code';
    // Default (SCR, NCR, ECR …) – blocks use station_code, regular stations use station_name
    if (isBlock) return 'station_code';
    return 'station_name';
  }

  private resolveRowDisplayName(row: any): string {
    if (typeof row?.station_name === 'object') {
      return row?.station_name?.station_name ?? '';
    }
    return row?.station_name ?? '';
  }

  /** Sync station_id change for the CURRENT station everywhere. */
  ssyncStationId(
    stationData: any,
    oldStationId: string,
    newStationId: string,
    alreadySynced = false
  ): void {
    if (!stationData || isNullOrUndefined(newStationId)) return;

    const currentStationId = stationData['Station ID'];
    stationData['Station ID'] = newStationId;
    stationData.station_meta_info?.forEach((item: any) => {
      if (
        item.type_of_asset === 'station_title' &&
        (String(item.station_id) === String(oldStationId) || String(item.station_id) === String(currentStationId))
      ) {
        item.station_id = newStationId;
      }
    });
    stationData.Signal?.forEach((track: any) => {
      track.assets?.forEach((asset: any) => {
        if (String(asset.beside_station_id) === String(oldStationId) || String(asset.beside_station_id) === String(currentStationId)) {
          asset.beside_station_id = newStationId;
        }
      });
    });
    if (stationData.ip_port_table?.data) {
      stationData.ip_port_table.data.forEach((row: any) => {
        if (String(row.station_id) === String(oldStationId) || String(row.station_id) === String(currentStationId) || this.isCurrentStation(stationData, row.station_id)) {
          row.station_id = newStationId;
        }
      });
      stationData.ip_port_table = {
        ...stationData.ip_port_table,
        data: [...stationData.ip_port_table.data]
      };
    }

    stationData.tower_plotting_info?.forEach((tower: any) => {
      if (String(tower.station_id) === String(oldStationId) || String(tower.station_id) === String(currentStationId)) {
        tower.station_id = newStationId;
      }
    });

    if (stationData.station_tag_summary?.stations) {
      stationData.station_tag_summary.stations.forEach((row: any) => {
        if (String(row.station_id) === String(oldStationId) || String(row.station_id) === String(currentStationId) || this.isCurrentStation(stationData, row.station_id)) {
          row.station_id = newStationId;
        }
      });
      stationData.station_tag_summary = {
        ...stationData.station_tag_summary,
        stations: [...stationData.station_tag_summary.stations]
      };
    }

    const nearest = stationData?.nearest_stations_info;
    if (nearest) {
      if (String(nearest.left_station_id) === String(oldStationId)) nearest.left_station_id = newStationId;
      if (String(nearest.right_station_id) === String(oldStationId)) nearest.right_station_id = newStationId;
    }

    const towerInfo = stationData?.['Tower Information'];
    if (towerInfo) {
      Object.keys(towerInfo).forEach(key => {
        const tower = towerInfo[key];
        if (String(tower.station_id) === String(oldStationId)) {
          tower.station_id = newStationId;
        }
      });
    }
    this.deepReplaceInStrings(stationData, oldStationId, newStationId);
    this.triggerRegeneration(stationData);

    if (this.stepperService.surveyData?.stationData) {
      this.stepperService.surveyData.stationData.station_id = newStationId;
    }

    if (!alreadySynced) {
      const mainData = this.stepperService.stationData;
      const draftData = this.stepperService.previewStationStore?.draftStationData;
      if (stationData === mainData && draftData && draftData !== mainData) {
        this.ssyncStationId(draftData, oldStationId, newStationId, true);
      } else if (stationData === draftData && mainData && mainData !== draftData) {
        this.ssyncStationId(mainData, oldStationId, newStationId, true);
      }
      this.stationDetailsUpdated$.next();
    }
  }

  syncAdjacentStationId(
    stationData: any,
    newId: string,
    stationUuid?: string,
    oldId?: string,
    alreadySynced = false
  ): void {
    if (!stationData || isNullOrUndefined(newId)) return;

    const adj = this.findAdjacentByUuidOrId(stationData, stationUuid, oldId);
    if (!adj) return;

    const oldActualId = adj.station_id;
    adj.station_id = newId;

    stationData?.ip_port_table?.data?.forEach((row: any) => {
      if (String(row.station_id) === String(oldActualId)) {
        row.station_id = newId;
      }
    });

    stationData.Signal?.forEach((signal: any) => {
      signal.assets?.forEach((asset: any) => {
        if (String(asset.beside_station_id) === String(oldActualId)) {
          asset.beside_station_id = newId;
        }
      });
    });

    stationData.station_tag_summary?.stations?.forEach((row: any) => {
      if (String(row.station_id) === String(oldActualId)) {
        row.station_id = newId;
      }
    });

    stationData.tower_plotting_info?.forEach((tower: any) => {
      if (String(tower.station_id) === String(oldActualId)) {
        tower.station_id = newId;
      }
    });

    const nearest = stationData?.nearest_stations_info;
    if (nearest) {
      if (String(nearest.left_station_id) === String(oldActualId)) nearest.left_station_id = newId;
      if (String(nearest.right_station_id) === String(oldActualId)) nearest.right_station_id = newId;
    }

    const towerInfo = stationData?.['Tower Information'];
    if (towerInfo) {
      Object.keys(towerInfo).forEach(key => {
        const tower = towerInfo[key];
        if (String(tower.station_id) === String(oldActualId)) {
          tower.station_id = newId;
        }
      });
    }

    this.deepReplaceInStrings(stationData, oldActualId, newId);
    this.triggerRegeneration(stationData);

    if (!alreadySynced) {
      const mainData = this.stepperService.stationData;
      const draftData = this.stepperService.previewStationStore?.draftStationData;
      if (stationData === mainData && draftData && draftData !== mainData) {
        this.syncAdjacentStationId(draftData, newId, adj.station_uuid, oldActualId, true);
      } else if (stationData === draftData && mainData && mainData !== draftData) {
        this.syncAdjacentStationId(mainData, newId, adj.station_uuid, oldActualId, true);
      }
      this.stationDetailsUpdated$.next();
    }
  }

  syncStationName(stationData: any, newStationName: string, alreadySynced = false): void {
    if (!stationData || isNullOrUndefined(newStationName)) return;

    const oldStationName = stationData['Station Name'] ?? '';
    const stationCode = stationData['Station Code'] ?? '';
    const stationId   = stationData['Station ID'];
    const combined    = `${newStationName} (${stationCode})`.trim();
    const zone: string = stationData['Zone'] ?? '';

    stationData['Station Name'] = newStationName;

    if (stationData.author_review_table?.station) {
      stationData.author_review_table.station.station_name = combined;
    }

    if (stationData.signal_telecommunication_double_data_table?.station_info) {
      stationData.signal_telecommunication_double_data_table.station_info.station_name_english = newStationName;
    }
    if (stationData.signal_telecommunication_single_data_table?.station_info) {
      stationData.signal_telecommunication_single_data_table.station_info.station_name_english = combined;
    }

    stationData.tower_plotting_info?.forEach((tower: any) => {
      if (String(tower.station_id) === String(stationId)) {
        tower.station_name = newStationName;
      }
    });

    const towerInfo = stationData?.['Tower Information'];
    if (towerInfo) {
      Object.keys(towerInfo).forEach(key => {
        const tower = towerInfo[key];
        if (String(tower.station_id) === String(stationId)) {
          tower.station_name = newStationName;
        }
      });
    }

    const nameField = this.nameFieldForZone(zone, true, false);
    if (stationData.ip_port_table?.data) {
      stationData.ip_port_table.data.forEach((row: any) => {
        if (String(row.station_id) === String(stationId) || this.isCurrentStation(stationData, row.station_id)) {
          row.station_name = newStationName;
        }
      });
      stationData.ip_port_table = {
        ...stationData.ip_port_table,
        data: [...stationData.ip_port_table.data]
      };
    }

    if (stationData.station_tag_summary?.stations) {
      stationData.station_tag_summary.stations.forEach((row: any) => {
        if (String(row.station_id) === String(stationId) || this.isCurrentStation(stationData, row.station_id)) {
          row.name = newStationName;
          row.station_name = newStationName;
        }
      });
      stationData.station_tag_summary = {
        ...stationData.station_tag_summary,
        stations: [...stationData.station_tag_summary.stations]
      };
    }
    this.deepReplaceInStrings(stationData, oldStationName, newStationName);
    this.triggerRegeneration(stationData);

    if (this.stepperService.surveyData?.stationData) {
      this.stepperService.surveyData.stationData.station_name = newStationName;
    }

    if (!alreadySynced) {
      const mainData = this.stepperService.stationData;
      const draftData = this.stepperService.previewStationStore?.draftStationData;
      if (stationData === mainData && draftData && draftData !== mainData) {
        this.syncStationName(draftData, newStationName, true);
      } else if (stationData === draftData && mainData && mainData !== draftData) {
        this.syncStationName(mainData, newStationName, true);
      }
      this.stationDetailsUpdated$.next();
    }
  }

  syncAdjacentStationName(
    stationData: any,
    newStationName: string,
    stationUuid?: string,
    stationId?: string,
    alreadySynced = false,
  ): void {
    if (!stationData || isNullOrUndefined(newStationName)) return;

    const zone: string = stationData['Zone'] ?? '';

    // Find adjacent station canonically
    const adj = this.findAdjacentByUuidOrId(stationData, stationUuid, stationId);
    if (!adj) return;

    const oldName = adj.station_name;
    adj.station_name = newStationName;

    // Update IP port table unconditionally
    stationData?.ip_port_table?.data?.forEach((row: any) => {
      if (String(row.station_id) === String(adj.station_id)) {
        row.station_name = newStationName;
      }
    });

    // Update nearest stations
    const nearest = stationData?.nearest_stations_info;
    if (nearest) {
      if (String(nearest.left_station_id) === String(adj.station_id)) {
        nearest.left_station_name = newStationName;
      }
      if (String(nearest.right_station_id) === String(adj.station_id)) {
        nearest.right_station_name = newStationName;
      }
    }

    // Update tower plotting
    stationData.tower_plotting_info?.forEach((tower: any) => {
      if (String(tower.station_id) === String(adj.station_id)) {
        tower.station_name = newStationName;
      }
    });

    // Update Tower Information
    const towerInfo = stationData?.['Tower Information'];
    if (towerInfo) {
      Object.keys(towerInfo).forEach(key => {
        const tower = towerInfo[key];
        if (String(tower.station_id) === String(adj.station_id)) {
          tower.station_name = newStationName;
        }
      });
    }

    // Update max CM map by old station name
    const maxCmMap = stationData?.max_cm_distances_map;
    if (Array.isArray(maxCmMap) && oldName) {
      const oldNameToMatch = oldName.trim().toLowerCase();
      maxCmMap.forEach((entry: any) => {
        if (entry?.station_name?.trim()?.toLowerCase() === oldNameToMatch) {
          entry.station_name = newStationName;
        }
      });
    }

    // Update tag summary unconditionally
    stationData?.station_tag_summary?.stations?.forEach((entry: any) => {
      if (String(entry.station_id) === String(adj.station_id)) {
        entry.name = newStationName;
        entry.station_name = newStationName;
      }
    });

    this.deepReplaceInStrings(stationData, oldName, newStationName);
    this.triggerRegeneration(stationData);

    if (!alreadySynced) {
      const mainData = this.stepperService.stationData;
      const draftData = this.stepperService.previewStationStore?.draftStationData;
      if (stationData === mainData && draftData && draftData !== mainData) {
        this.syncAdjacentStationName(draftData, newStationName, adj.station_uuid, adj.station_id, true);
      } else if (stationData === draftData && mainData && mainData !== draftData) {
        this.syncAdjacentStationName(mainData, newStationName, adj.station_uuid, adj.station_id, true);
      }
      this.stationDetailsUpdated$.next();
    }
  }

  syncStationCode(stationData: any, newStationCode: string, alreadySynced = false): void {
    if (!stationData || isNullOrUndefined(newStationCode)) return;

    const oldStationCode = stationData['Station Code'] ?? '';
    const stationName = stationData['Station Name'] ?? '';
    const stationId   = stationData['Station ID'];
    const combined    = `${stationName} (${newStationCode})`.trim();
    const zone: string = stationData['Zone'] ?? '';
    const z = zone.trim().toLowerCase();

    stationData['Station Code'] = newStationCode;

    stationData.station_meta_info?.forEach((item: any) => {
      if (
        item.type_of_asset === 'station_title' &&
        String(item.station_id) === String(stationId)
      ) {
        item.station_code = newStationCode;
      }
    });

    if (stationData.author_review_table?.station) {
      stationData.author_review_table.station.station_code = newStationCode;
      stationData.author_review_table.station.station_name = combined;
    }

    if (stationData.signal_telecommunication_single_data_table?.station_info) {
      stationData.signal_telecommunication_single_data_table.station_info.station_name_english = combined;
    }

    const nameField = this.nameFieldForZone(zone, true, false);
    stationData.ip_port_table?.data?.forEach((row: any) => {
      if (String(row.station_id) === String(stationId) || this.isCurrentStation(stationData, row.station_id)) {
        row.station_code = newStationCode;
      }
    });

    stationData.tower_plotting_info?.forEach((tower: any) => {
      if (String(tower.station_id) === String(stationId)) {
        tower.station_code = newStationCode;
      }
    });

    const towerInfo = stationData?.['Tower Information'];
    if (towerInfo) {
      Object.keys(towerInfo).forEach(key => {
        const tower = towerInfo[key];
        if (String(tower.station_id) === String(stationId)) {
          tower.station_code = newStationCode;
        }
      });
    }

    stationData.station_tag_summary?.stations?.forEach((row: any) => {
      if (String(row.station_id) === String(stationId) || this.isCurrentStation(stationData, row.station_id)) {
        row.station_code = newStationCode;
        if (nameField === 'station_code') {
          row.name = newStationCode;
        }
      }
    });

    this.deepReplaceInStrings(stationData, oldStationCode, newStationCode);
    this.triggerRegeneration(stationData);

    if (this.stepperService.surveyData?.stationData) {
      this.stepperService.surveyData.stationData.station_code = newStationCode;
    }

    if (!alreadySynced) {
      const mainData = this.stepperService.stationData;
      const draftData = this.stepperService.previewStationStore?.draftStationData;
      if (stationData === mainData && draftData && draftData !== mainData) {
        this.syncStationCode(draftData, newStationCode, true);
      } else if (stationData === draftData && mainData && mainData !== draftData) {
        this.syncStationCode(mainData, newStationCode, true);
      }
      this.stationDetailsUpdated$.next();
    }
  }

  syncAdjacentStationCode(
    stationData: any,
    newStationCode: string,
    stationUuid?: string,
    stationId?: string,
    alreadySynced = false
  ): void {
    if (!stationData || isNullOrUndefined(newStationCode)) return;

    const zone: string = stationData['Zone'] ?? '';

    // Find adjacent station canonically
    const adj = this.findAdjacentByUuidOrId(stationData, stationUuid, stationId);
    if (!adj) return;

    const oldCode = adj.station_code ?? '';
    adj.station_code = newStationCode;

    // Update IP port table row station_code
    stationData?.ip_port_table?.data?.forEach((row: any) => {
      if (String(row.station_id) === String(adj.station_id)) {
        row.station_code = newStationCode;
      }
    });

    // Update tower plotting info
    stationData.tower_plotting_info?.forEach((tower: any) => {
      if (String(tower.station_id) === String(adj.station_id)) {
        tower.station_code = newStationCode;
      }
    });

    const towerInfo = stationData?.['Tower Information'];
    if (towerInfo) {
      Object.keys(towerInfo).forEach(key => {
        const tower = towerInfo[key];
        if (String(tower.station_id) === String(adj.station_id)) {
          tower.station_code = newStationCode;
        }
      });
    }

    // Update tag summary
    stationData?.station_tag_summary?.stations?.forEach((entry: any) => {
      if (String(entry.station_id) === String(adj.station_id)) {
        entry.station_code = newStationCode;
        const tagSummaryDisplayedField = this.nameFieldForZone(zone, false, adj.is_block_section_station ?? false);
        if (tagSummaryDisplayedField === 'station_code') {
          entry.name = newStationCode;
        }
      }
    });

    this.deepReplaceInStrings(stationData, oldCode, newStationCode);
    this.triggerRegeneration(stationData);

    if (!alreadySynced) {
      const mainData = this.stepperService.stationData;
      const draftData = this.stepperService.previewStationStore?.draftStationData;
      if (stationData === mainData && draftData && draftData !== mainData) {
        this.syncAdjacentStationCode(draftData, newStationCode, adj.station_uuid, adj.station_id, true);
      } else if (stationData === draftData && mainData && mainData !== draftData) {
        this.syncAdjacentStationCode(mainData, newStationCode, adj.station_uuid, adj.station_id, true);
      }
      this.stationDetailsUpdated$.next();
    }
  }

  syncStationTitle(stationData: any, newTitle: string, alreadySynced = false): void {
    if (!stationData || isNullOrUndefined(newTitle)) return;

    const oldTitle = stationData['Station Title'] ?? '';
    stationData['Station Title'] = newTitle;

    stationData.station_meta_info?.forEach((item: any) => {
      if (item.type_of_asset === 'station_title') {
        item.station_title = newTitle;
      }
    });

    if (stationData.author_review_table?.station) {
      stationData.author_review_table.station.station_title = newTitle;
    }

    if (this.stepperService.surveyData?.stationData) {
      this.stepperService.surveyData.stationData.station_title = newTitle;
    }

    this.deepReplaceInStrings(stationData, oldTitle, newTitle);
    this.triggerRegeneration(stationData);

    if (!alreadySynced) {
      const mainData = this.stepperService.stationData;
      const draftData = this.stepperService.previewStationStore?.draftStationData;
      if (stationData === mainData && draftData && draftData !== mainData) {
        this.syncStationTitle(draftData, newTitle, true);
      } else if (stationData === draftData && mainData && mainData !== draftData) {
        this.syncStationTitle(mainData, newTitle, true);
      }
      this.stationDetailsUpdated$.next();
    }
  }

  syncSection(stationData: any, newSection: string, alreadySynced = false): void {
    if (!stationData || isNullOrUndefined(newSection)) return;

    stationData['Section'] = newSection;
    stationData['section'] = newSection;
    if ('section_type' in stationData) {
      stationData['section_type'] = newSection;
    }

    if (stationData.author_review_table?.station) {
      stationData.author_review_table.station.section = newSection;
    }

    if (stationData.signal_telecommunication_single_data_table?.station_info) {
      stationData.signal_telecommunication_single_data_table.station_info.section = newSection;
    }

    if (stationData.signal_telecommunication_double_data_table?.station_info) {
      stationData.signal_telecommunication_double_data_table.station_info.section = newSection;
    }

    if (this.stepperService.surveyData?.stationData) {
      this.stepperService.surveyData.stationData.section = newSection;
      (this.stepperService.surveyData.stationData as any).section_name = newSection;
    }

    this.triggerRegeneration(stationData);

    if (!alreadySynced) {
      const mainData = this.stepperService.stationData;
      const draftData = this.stepperService.previewStationStore?.draftStationData;
      if (stationData === mainData && draftData && draftData !== mainData) {
        this.syncSection(draftData, newSection, true);
      } else if (stationData === draftData && mainData && mainData !== draftData) {
        this.syncSection(mainData, newSection, true);
      }
      this.stationDetailsUpdated$.next();
    }
  }

  /**
   * Deep-walk the entire stationData object and replace occurrences of
   * oldValue with newValue inside ANY string property — including
   * embedded occurrences like "BMM EXIT &<br>LPRA ENTRY BORDER",
   * block section names like "BHASKARPARA-LILAPUR ROADA",
   * block section IDs like "25502-25603", comm_start, comm_end, etc.
   *
   * Uses word-boundary matching (\b) so that "DEV123" won't accidentally
   * match inside "DEV1234" — only at real boundaries (spaces, hyphens,
   * start/end of string, brackets, etc.).
   */
  private deepReplaceInStrings(
    obj: any,
    oldValue: string,
    newValue: string,
    visited = new WeakSet()
  ): void {
    if (!oldValue || oldValue === newValue) return;
    if (obj == null || typeof obj !== 'object') return;
    if (visited.has(obj)) return;
    visited.add(obj);

    const escaped = oldValue.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`\\b${escaped}\\b`, 'g');

    if (Array.isArray(obj)) {
      for (let i = 0; i < obj.length; i++) {
        if (typeof obj[i] === 'string' && regex.test(obj[i])) {
          regex.lastIndex = 0;
          obj[i] = obj[i].replace(regex, newValue);
        } else if (typeof obj[i] === 'object') {
          this.deepReplaceInStrings(obj[i], oldValue, newValue, visited);
        }
      }
    } else {
      for (const key of Object.keys(obj)) {
        const val = obj[key];
        if (typeof val === 'string' && regex.test(val)) {
          regex.lastIndex = 0;
          obj[key] = val.replace(regex, newValue);
        } else if (typeof val === 'object') {
          this.deepReplaceInStrings(val, oldValue, newValue, visited);
        }
      }
    }
  }

  private triggerRegeneration(stationData: any): void {
    if (!stationData) return;
    if (!stationData.operations_list) {
      stationData.operations_list = [];
    }
    if (!stationData.operations_list.includes('Update Inputs')) {
      stationData.operations_list.push('Update Inputs');
    }
  }

  /**
   * Deep-clone stationData for use as a rollback snapshot.
   * Callers should call this BEFORE any handler, then call restoreStationData()
   * if the downstream API call fails.
   */
  snapshotStationData(stationData: any): any {
    return structuredClone(stationData);
  }

  /**
   * Restore all properties of stationData from a previously taken snapshot.
   * Call this in the API error handler to undo all sync mutations.
   */
  restoreStationData(stationData: any, snapshot: any): void {
    // Remove keys not in snapshot
    Object.keys(stationData).forEach(k => {
      if (!(k in snapshot)) delete stationData[k];
    });
    // Restore all keys from snapshot (deep assign)
    Object.assign(stationData, structuredClone(snapshot));
  }

  /**
   * Returns true if change was applied, false if a local duplicate was detected
   * (no mutations occur in that case).
   */
  handleIpPortStationIdChange(
    stationData: any,
    row: any,
    newStationId: string
  ): true | string {
    const oldId = row.station_id;
    if (String(oldId) === String(newStationId)) return true;

    const isEditingMain = this.isCurrentStation(stationData, oldId);

    // ── Duplicate checks against canonical adjacent_stations list ──
    if (isEditingMain) {
      const dup = stationData?.adjacent_stations?.find(
        (s: any) => String(s.station_id) === String(newStationId)
      );
      if (dup) {
        return `Station ID "${newStationId}" is already used by an adjacent station ("${dup.station_name || dup.station_id}"). Please enter a unique ID.`;
      }
    } else {
      const currentStationId = String(stationData?.['Station ID'] ?? '');
      if (currentStationId && String(newStationId) === currentStationId) {
        return `Station ID "${newStationId}" is already used by the current station. Please enter a unique ID.`;
      }
      const dup = stationData?.adjacent_stations?.find(
        (s: any) => String(s.station_id) !== String(oldId) && String(s.station_id) === String(newStationId)
      );
      if (dup) {
        return `Station ID "${newStationId}" is already used by another adjacent station ("${dup.station_name || dup.station_id}"). Please enter a unique ID.`;
      }
    }

    // ── Apply changes ──
    if (isEditingMain) {
      this.ssyncStationId(stationData, oldId, newStationId);
    } else {
      const adj = this.findAdjacentById(stationData, oldId);
      this.syncAdjacentStationId(stationData, newStationId, adj?.station_uuid, oldId);
    }
    row.station_id = newStationId;
    return true;
  }

  /**
   * Returns true if change was applied, string error if a duplicate was detected.
   *
   * KEY RULE: The ip_port_table shows station_code for WR, station_name otherwise.
   * We only sync the field that THIS table is actually displaying — never blindly
   * sync both name and code together across tables.
   */
  handleIpPortStationNameChange(
    stationData: any,
    row: any,
    newValue: string
  ): true | string {
    const isCurrent = this.isCurrentStation(stationData, row.station_id);
    const adj = isCurrent ? null : this.findAdjacentById(stationData, row.station_id);

    const zone: string = stationData['Zone'] ?? '';
    const isBlock = adj?.is_block_section_station ?? false;
    const nameField = this.nameFieldForZone(zone, isCurrent, isBlock);

    if (nameField === 'station_code') {
      if (isCurrent) {
        this.syncStationCode(stationData, newValue);
      } else {
        const stationUuid = adj?.station_uuid;
        this.syncAdjacentStationCode(stationData, newValue, stationUuid, row.station_id);
      }
      row.station_code = newValue;
    } else {
      if (isCurrent) {
        this.syncStationName(stationData, newValue);
      } else {
        const stationUuid = adj?.station_uuid;
        this.syncAdjacentStationName(stationData, newValue, stationUuid, row.station_id);
      }
      row.station_name = newValue;
    }
    return true;
  }

  handleIpPortStationCodeChange(
    stationData: any,
    row: any,
    newValue: string
  ): true | string {
    const isCurrent = this.isCurrentStation(stationData, row.station_id);
    const adj = isCurrent ? null : this.findAdjacentById(stationData, row.station_id);

    // ── Duplicate check for Station Code ──
    if (!isCurrent) {
      const currentCode = String(stationData?.['Station Code'] ?? '').trim().toLowerCase();
      if (currentCode && newValue.trim().toLowerCase() === currentCode) {
        return `Station Code "${newValue}" is already used by the current station.`;
      }
      const dup = stationData?.adjacent_stations?.find((s: any) => {
        if (String(s.station_id) === String(row.station_id)) return false;
        return String(s.station_code ?? '').trim().toLowerCase() === newValue.trim().toLowerCase();
      });
      if (dup) {
        return `Station Code "${newValue}" is already used by another adjacent station.`;
      }
    }

    // ── Apply changes — strictly sync station_code ──
    if (isCurrent) {
      this.syncStationCode(stationData, newValue);
    } else {
      const stationUuid = adj?.station_uuid;
      this.syncAdjacentStationCode(stationData, newValue, stationUuid, row.station_id);
    }
    row.station_code = newValue;
    return true;
  }

  /**
   * Returns true if change was applied, false if a local duplicate was detected.
   */
  handleTagTableStationIdChange(
    stationData: any,
    st: any,
    newStationId: string
  ): true | string {
    const oldId = st.station_id;
    if (String(oldId) === String(newStationId)) return true;

    const isEditingMain = this.isCurrentStation(stationData, oldId);

    // ── Duplicate checks against canonical adjacent_stations list ──
    if (isEditingMain) {
      const dup = stationData?.adjacent_stations?.find(
        (s: any) => String(s.station_id) === String(newStationId)
      );
      if (dup) {
        return `Station ID "${newStationId}" is already used by an adjacent station ("${dup.station_name || dup.station_id}"). Please enter a unique ID.`;
      }
    } else {
      const currentStationId = String(stationData?.['Station ID'] ?? '');
      if (currentStationId && String(newStationId) === currentStationId) {
        return `Station ID "${newStationId}" is already used by the current station. Please enter a unique ID.`;
      }
      const dup = stationData?.adjacent_stations?.find(
        (s: any) => String(s.station_id) !== String(oldId) && String(s.station_id) === String(newStationId)
      );
      if (dup) {
        return `Station ID "${newStationId}" is already used by another adjacent station ("${dup.station_name || dup.station_id}"). Please enter a unique ID.`;
      }
    }

    // ── Apply changes ──
    if (isEditingMain) {
      this.ssyncStationId(stationData, oldId, newStationId);
    } else {
      this.syncAdjacentStationId(stationData, newStationId, st.station_uuid, oldId);
    }
    st.station_id = newStationId;
    return true;
  }

  /**
   * Returns true if change was applied, string error if a duplicate was detected.
   *
   * KEY RULE: The tag summary table shows station_code or station_name depending
   * on zone/isBlock. We only sync the field that THIS table is actually displaying.
   * Editing station_name in the tag table must NOT update station_code in the ip_port
   * table, and vice versa.
   */
  handleTagTableNameChange(
    stationData: any,
    st: any,
    newName: string
  ): true | string {
    const isCurrent = this.isCurrentStation(stationData, st.station_id);
    const adj = isCurrent ? null : this.findAdjacentById(stationData, st.station_id);

    const zone: string = stationData['Zone'] ?? '';
    const isBlock = st?.is_block_section_station ?? adj?.is_block_section_station ?? false;
    const nameField = this.nameFieldForZone(zone, isCurrent, isBlock);

    if (nameField === 'station_code') {
      if (isCurrent) {
        this.syncStationCode(stationData, newName);
      } else {
        const stationUuid = adj?.station_uuid;
        this.syncAdjacentStationCode(stationData, newName, stationUuid, st.station_id);
      }
      st.station_code = newName;
    } else {
      if (isCurrent) {
        this.syncStationName(stationData, newName);
      } else {
        const stationUuid = adj?.station_uuid;
        this.syncAdjacentStationName(stationData, newName, stationUuid, st.station_id);
      }
      st.station_name = newName;
    }
    st.name = newName;
    return true;
  }

  handleTagTableCodeChange(
    stationData: any,
    st: any,
    newCode: string
  ): true | string {
    const isCurrent = this.isCurrentStation(stationData, st.station_id);
    const adj = isCurrent ? null : this.findAdjacentById(stationData, st.station_id);

    // ── Duplicate check for Station Code ──
    if (!isCurrent) {
      const currentCompare = String(stationData?.['Station Code'] ?? '').trim().toLowerCase();
      if (currentCompare && newCode.trim().toLowerCase() === currentCompare) {
        return `Station Code "${newCode}" is already used by the current station.`;
      }
      const dup = stationData?.adjacent_stations?.find((s: any) => {
        if (String(s.station_id) === String(st.station_id)) return false;
        const val = String(s.station_code ?? '').trim().toLowerCase();
        return val === newCode.trim().toLowerCase();
      });
      if (dup) {
        return `Station Code "${newCode}" is already used by another adjacent station.`;
      }
    }

    // ── Apply changes — strictly sync station_code ──
    if (isCurrent) {
      this.syncStationCode(stationData, newCode);
    } else {
      const stationUuid = adj?.station_uuid;
      this.syncAdjacentStationCode(stationData, newCode, stationUuid, st.station_id);
    }
    st.name = newCode;
    return true;
  }

  /**
   * Cleans a division string by stripping comma-separated zone/railway suffixes,
   * redundant "division" keywords, and excessive whitespace.
   */
  static cleanDivisionName(rawDivision: any): string {
    if (typeof rawDivision !== 'string' || !rawDivision.trim()) return '';
    if (rawDivision.includes(',') || /\bdivision\b/i.test(rawDivision)) {
      return rawDivision.split(',')[0].replace(/\bdivision\b/gi, '').trim();
    }
    return rawDivision.trim();
  }

  cleanDivisionName(rawDivision: any): string {
    return StationDetailsSyncService.cleanDivisionName(rawDivision);
  }

  /**
   * Sanitizes target['Station Division'] in-place if present and composite/corrupted.
   */
  static sanitizeStationDivision(target: any): void {
    if (!target) return;
    const clean = StationDetailsSyncService.cleanDivisionName(target['Station Division']);
    if (clean && clean !== target['Station Division']) {
      target['Station Division'] = clean;
    }
  }

  sanitizeStationDivision(target: any): void {
    StationDetailsSyncService.sanitizeStationDivision(target);
  }

  /**
   * Formats a clean division name for the Author Review Table by preserving
   * the existing casing of the 'Division' keyword and any trailing zone details.
   */
  static formatAuthorTableDivision(value: string, existingAuthorDivision?: string): string {
    if (!existingAuthorDivision) {
      const cleanVal = StationDetailsSyncService.cleanDivisionName(value) || String(value ?? '').trim();
      return cleanVal ? `${cleanVal} Division` : '';
    }
    const divisionParts = existingAuthorDivision.split(',');
    const rest = divisionParts.slice(1).join(',');
    const existingDivisionWord = divisionParts[0]?.match(/\bdivision\b/i)?.[0] || 'Division';
    const cleanVal = StationDetailsSyncService.cleanDivisionName(value) || String(value ?? '').trim();
    const divisionLabel = cleanVal ? `${cleanVal} ${existingDivisionWord}` : '';
    return rest ? `${divisionLabel},${rest}` : divisionLabel;
  }

  formatAuthorTableDivision(value: string, existingAuthorDivision?: string): string {
    return StationDetailsSyncService.formatAuthorTableDivision(value, existingAuthorDivision);
  }

  private plainSipText(value: any): string {
    if (value == null) return '';
    if (Array.isArray(value?.segments)) {
      const hasOldText = value.segments.some((s: any) => s.styleId === 'success');
      const hasNewText = value.segments.some((s: any) => s.styleId === 'danger');
      const segments = (hasOldText && hasNewText)
        ? value.segments.filter((s: any) => s.styleId !== 'success')
        : value.segments;
      return segments.map((s: any) => s.text).join('');
    }
    return String(value);
  }

  syncSipReference(stationData: any, value: any): void {
    if (!stationData) return;
    const plainSip = this.plainSipText(value);
    const sipNumber = plainSip?.substring(plainSip?.lastIndexOf(':') + 1)?.replace('SG(SP)', '')?.trim();

    if (stationData.author_review_table?.ref) stationData.author_review_table.ref.sip_value = plainSip;
    stationData['REF SIP'] = plainSip;

    const doubleTable = stationData.signal_telecommunication_double_data_table;
    if (doubleTable?.top_left_info) doubleTable.top_left_info.sip = sipNumber;
    if (doubleTable?.sheet_info?.[1]?.systemRef) doubleTable.sheet_info[1].systemRef.sip = sipNumber;
    if (doubleTable?.left_panel?.lines?.[1] != null) doubleTable.left_panel.lines[1] = `S.I.P. ${sipNumber}`;

    const singleTable = stationData.signal_telecommunication_single_data_table;
    if (singleTable?.sheet_info?.[1]?.kavachRef) singleTable.sheet_info[1].kavachRef.value = sipNumber ? `SIP:${sipNumber}` : '';

    const refSigRight = stationData.signal_telecommunication_ref_sig_plan_table?.left_section?.header?.right;
    if (refSigRight) refSigRight.ref_sig_plan = plainSip;
  }


  syncSipReferenceFromNumber(stationData: any, rawValue: string): void {
    const plain = String(rawValue ?? '')?.replace(/^SIP:?\s*/i, '')?.trim();
    this.syncSipReference(stationData, plain ? `SIP : ${plain}` : '');
  }
}